<script setup lang="ts">
import { ref } from 'vue';
import { NxtModal, NxtButton, NxtSpinner } from '@future-harry/nxt-styles';
import type { ApplyFormModel } from './apply.model';
import { defaultApplyForm } from './apply.model';
import ApplyForm from '../../forms/ApplyForm.vue';
import { usePositionApplication } from '../../../composables/usePositionApplication';
import type { ApplicationFields } from '@/core/collections/job-boards/job-board.interface';

const props = defineProps<{
   applicationFields: ApplicationFields;
   positionId: string;
}>();
const emit = defineEmits<{
   (e: 'onClose'): void;
}>();

const form = ref<ApplyFormModel>(defaultApplyForm());
const { isSubmitting, isSuccess, errors, submit: submitApplication } = usePositionApplication(form, props.applicationFields);

async function submit() {
   await submitApplication(props.positionId, form.value);
}

function cancel() {
   emit('onClose');
}
</script>

<template>
   <NxtModal
      :title="$tb('public.apply.title')"
      :primary-action-text="$tb('public.apply.primary')"
      :cancel-text="$tb('public.apply.cancel')"
      :has-cancel-button="!isSubmitting && isSuccess !== true"
      :has-primary-action="!isSubmitting && isSuccess !== true"
      @on-primary-action="submit"
      @on-cancel="cancel"
   >
      <div class="apply-modal">
         <div v-if="!isSubmitting && isSuccess !== true">
            <div v-if="isSuccess === false" class="error-msg">{{ $tb('public.apply.error_message') }}</div>
            <ApplyForm v-model="form" :application-fields="applicationFields" :errors="errors" />
         </div>
         <div v-else-if="isSubmitting" class="apply-modal__result">
            <NxtSpinner />
         </div>
         <div v-else-if="isSuccess === true" class="apply-modal__result">
            <div>{{ $tb('public.apply.success_message') }}</div>
            <NxtButton :button-type="'primary'" @click="cancel">{{ $tb('public.apply.close') }}</NxtButton>
         </div>
      </div>
   </NxtModal>
</template>

<style scoped lang="scss">
.error-msg {
   color: var(--nxt-red);
   margin-bottom: var(--nxt-gutter);
}

.apply-modal__result {
   display: flex;
   flex-direction: column;
   align-items: center;
   text-align: center;
   gap: 1rem;
}

:deep(.nxt-spinner) {
   color: var(--jobsite-primary);
}

:deep(.nxt-button--info) {
   background-color: transparent;
   border-color: transparent;

   &:hover:not(:disabled) {
      border-color: var(--jobsite-primary);
   }
}

// NxtButtonTheme
:deep(.nxt-button--primary) {
   position: relative;
   background-color: var(--jobsite-primary);
   color: var(--jobsite-primary-font);
   border: 0;
   transition: box-shadow 0.2s ease;

   &::after {
      position: absolute;
      content: '';
      width: 100%;
      height: 0;
      bottom: 0;
      left: 0;
      background-color: rgba(255, 255, 255, 0.15);
   }

   &:hover:not(:disabled) {
      &:after {
         height: 100%;
      }
   }
}
</style>
